import type { ComponentType, ReactNode, SVGProps } from 'react';
import { Link } from 'react-router-dom';

/*
 * Topbar leading-slot patterns:
 * - <ListLeading> for index pages: icon + title + count badge.
 * - <BreadcrumbLeading> for detail / form pages: an icon and a chain of crumbs.
 */

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

type ListLeadingProps = {
  readonly title: string;
  readonly count: number;
  readonly icon?: IconComponent;
};

export function ListLeading({ title, count, icon: Icon }: ListLeadingProps) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      {Icon !== undefined && <Icon className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />}
      <h1 className="truncate text-base font-semibold text-text-primary">{title}</h1>
      <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-pill bg-subtle px-1.5 text-sm font-medium tabular-nums text-text-tertiary">
        {count}
      </span>
    </div>
  );
}

type ColumnLabelProps = {
  readonly icon?: IconComponent;
  readonly labelClassName?: string;
  readonly children: ReactNode;
};

export function ColumnLabel({ icon: Icon, labelClassName, children }: ColumnLabelProps) {
  return (
    <span className="inline-flex items-center gap-2">
      {Icon !== undefined && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      <span className={labelClassName}>{children}</span>
    </span>
  );
}

type Crumb = {
  readonly label: string;
  readonly to?: string;
};

type BreadcrumbLeadingProps = {
  readonly crumbs: ReadonlyArray<Crumb>;
  readonly icon?: IconComponent;
};

export function BreadcrumbLeading({ crumbs, icon: Icon }: BreadcrumbLeadingProps) {
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-base">
      {Icon !== undefined && <Icon className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />}
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;

        return (
          <div key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-2">
            {crumb.to !== undefined && !isLast ? (
              <Link to={crumb.to} className="truncate text-text-tertiary hover:text-text-primary hover:underline">
                {crumb.label}
              </Link>
            ) : (
              <span
                aria-current={isLast ? 'page' : undefined}
                className={`truncate ${isLast ? 'font-semibold text-text-primary' : 'text-text-tertiary'}`}
              >
                {crumb.label}
              </span>
            )}
            {!isLast && (
              <span aria-hidden className="text-text-placeholder">
                /
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
}
